/**
 * Other people's hands, drawn on the board (SRS D2, docs/06-UI-SPEC.md §15.1).
 *
 * Three things this file exists to get right:
 *
 *  - a cursor never teleports. Positions arrive a few times a second; each one is
 *    walked towards rather than jumped to, so a hand crossing the board reads as a
 *    hand crossing the board.
 *  - it shows the tool that person is holding, so a student can see what the teacher
 *    is about to do before they do it.
 *  - it fades after three seconds of stillness, so a class of twelve does not leave
 *    the board covered in name tags.
 *
 * All of the artwork and labelling here is Layer 3 — freely changeable.
 */

import { CURSOR_FADE_MS, interpolateCursor, presenceLabel } from '@opal/board-doc';
import { canvasToScreen, type Point, type Viewport } from '@opal/shared';
import type { PresenceState } from '@opal/board-doc';

import { ARROW_PATH, ERASER_PATH, HAND_PATH, MARKER_PATH } from './cursor.js';
import { cssColor } from './palette.js';

/** How long the fade itself takes, once the three still seconds are up. */
export const CURSOR_FADE_OUT_MS = 400;

/** How far a cursor closes the gap to its true position each frame. */
export const CURSOR_EASING = 0.28;

/** Below this, in canvas units, a cursor has arrived and can stop moving. */
const ARRIVED = 0.05;

/** The teacher is always this colour; students take the next free one in turn. */
const TEACHER_COLOR = { token: '--color-teacher-cursor', fallback: '#e5484d' };

const STUDENT_COLORS = [
  { token: '--color-student-cursor-1', fallback: '#2e8fe5' },
  { token: '--color-student-cursor-2', fallback: '#12b5a8' },
  { token: '--color-student-cursor-3', fallback: '#f5a524' },
  { token: '--color-student-cursor-4', fallback: '#a855c7' },
  { token: '--color-student-cursor-5', fallback: '#e5793a' },
];

/** A steady number from an id, so one person keeps one colour all lesson. */
function hashOf(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) hash = (hash * 31 + value.charCodeAt(i)) % 100_000;
  return hash;
}

/** The colour token a person's cursor is drawn in. */
export function cursorColorToken(state: PresenceState): string {
  if (state.role === 'teacher' || state.role === 'admin') return TEACHER_COLOR.token;
  const choice = STUDENT_COLORS[hashOf(state.userId) % STUDENT_COLORS.length];
  return choice?.token ?? TEACHER_COLOR.token;
}

function colorOf(state: PresenceState): string {
  const token = cursorColorToken(state);
  const known = [TEACHER_COLOR, ...STUDENT_COLORS].find((entry) => entry.token === token);
  return cssColor(token, known?.fallback ?? TEACHER_COLOR.fallback);
}

/**
 * How solid a cursor is right now.
 *
 * Fully there while the hand is moving, then gone a moment after it stops (SRS D2).
 */
export function cursorAlpha(lastMovedAt: number, now: number): number {
  const still = now - lastMovedAt;
  if (still <= CURSOR_FADE_MS) return 1;
  const faded = (still - CURSOR_FADE_MS) / CURSOR_FADE_OUT_MS;
  return faded >= 1 ? 0 : 1 - faded;
}

interface Drawn {
  readonly userId: string;
  label: string;
  color: string;
  toolId: string;
  /** Where it is being drawn, which lags where it really is. Canvas coordinates. */
  shown: Point;
  target: Point;
  lastMovedAt: number;
}

export interface RemoteCursors {
  /** Takes the latest word on where everybody is. */
  update(states: readonly PresenceState[]): void;
  /** Moves every cursor a step closer to where it is. True while anything is live. */
  step(now: number): boolean;
  paint(ctx: CanvasRenderingContext2D, viewport: Viewport, now: number): void;
  /** The cursors currently held, for tests and for the participants panel later. */
  readonly shown: readonly { userId: string; at: Point; label: string }[];
}

function adopt(existing: Drawn | undefined, state: PresenceState, cursor: Point): Drawn {
  if (!existing) {
    return {
      userId: state.userId,
      label: presenceLabel(state),
      color: colorOf(state),
      toolId: state.toolId,
      // A cursor appears where it is; only later movements are eased.
      shown: cursor,
      target: cursor,
      lastMovedAt: state.lastMovedAt,
    };
  }
  existing.label = presenceLabel(state);
  existing.color = colorOf(state);
  existing.toolId = state.toolId;
  existing.target = cursor;
  existing.lastMovedAt = state.lastMovedAt;
  return existing;
}

const TOOL_PATHS: Readonly<Record<string, string>> = {
  pen: HAND_PATH,
  highlighter: MARKER_PATH,
  eraser: ERASER_PATH,
  select: ARROW_PATH,
};

/** Where the drawing point sits inside the 24-unit glyph, per tool. */
const TOOL_HOTSPOTS: Readonly<Record<string, Point>> = {
  pen: { x: 10, y: 2 },
  highlighter: { x: 3, y: 21 },
  eraser: { x: 12, y: 12 },
  select: { x: 3, y: 2 },
};

function drawGlyph(ctx: CanvasRenderingContext2D, toolId: string, at: Point, color: string): void {
  const path = new Path2D(TOOL_PATHS[toolId] ?? HAND_PATH);
  const hotspot = TOOL_HOTSPOTS[toolId] ?? TOOL_HOTSPOTS.pen ?? { x: 10, y: 2 };
  ctx.save();
  ctx.translate(at.x - hotspot.x, at.y - hotspot.y);
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 1.2;
  ctx.lineJoin = 'round';
  ctx.fill(path);
  ctx.stroke(path);
  ctx.restore();
}

/** The name tag, sitting just below and right of the hand. */
function drawLabel(ctx: CanvasRenderingContext2D, label: string, at: Point, color: string): void {
  ctx.save();
  ctx.font = '500 12px system-ui, sans-serif';
  ctx.textBaseline = 'middle';
  const width = ctx.measureText(label).width + 12;
  const x = at.x + 14;
  const y = at.y + 16;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.roundRect(x, y, width, 18, 9);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.fillText(label, x + 6, y + 9);
  ctx.restore();
}

/** Holds everyone else's cursors and keeps them moving smoothly. */
export function createRemoteCursors(): RemoteCursors {
  const cursors = new Map<string, Drawn>();

  return {
    update(states) {
      const seen = new Set<string>();
      for (const state of states) {
        if (!state.cursor) continue;
        seen.add(state.userId);
        cursors.set(state.userId, adopt(cursors.get(state.userId), state, state.cursor));
      }
      // Somebody who has left, or whose hand has left the board, takes their cursor.
      for (const userId of [...cursors.keys()]) {
        if (!seen.has(userId)) cursors.delete(userId);
      }
    },

    step(now) {
      let live = false;
      for (const cursor of cursors.values()) {
        const gap = Math.hypot(cursor.target.x - cursor.shown.x, cursor.target.y - cursor.shown.y);
        if (gap > ARRIVED) {
          cursor.shown = interpolateCursor(cursor.shown, cursor.target, CURSOR_EASING);
          live = true;
        }
        // A cursor still fading needs frames too, right up until it is gone.
        if (cursorAlpha(cursor.lastMovedAt, now) > 0) live = true;
      }
      return live;
    },

    paint(ctx, viewport, now) {
      for (const cursor of cursors.values()) {
        const alpha = cursorAlpha(cursor.lastMovedAt, now);
        if (alpha <= 0) continue;
        const at = canvasToScreen(viewport, cursor.shown);
        ctx.save();
        ctx.globalAlpha = alpha;
        drawGlyph(ctx, cursor.toolId, at, cursor.color);
        drawLabel(ctx, cursor.label, at, cursor.color);
        ctx.restore();
      }
    },

    get shown() {
      return [...cursors.values()].map((cursor) => ({
        userId: cursor.userId,
        at: cursor.shown,
        label: cursor.label,
      }));
    },
  };
}
